import React, { useState, useEffect } from 'react';
import { Search, Home, Shield, ArrowRight, X, Phone } from 'lucide-react';
import { PORTAL_BASE_URL } from '../../../utils/cn';

// /support — section 2 ("What do you need help with?"), rebuilt
// 2026-05-24 from the user's mockup to replace the legacy
// SupportChannels grid. 3-lane card row matching user-journey
// stages: shopping → in-progress → post-install. Each card is
// clickable and opens a small modal offering two stage-appropriate
// actions (learn / portal / phone).
//
// Pass 2 (2026-05-24): card hover lifts + arrow-slide on bullets,
// and click-to-open action modal per card.

const PHONE_TEL = '9379108791';
const PHONE_DISPLAY = '(937) 910-8791';
const PORTAL_URL = PORTAL_BASE_URL; // env-driven (VITE_PORTAL_URL), single source in utils/cn.js

const LANES = [
  {
    id: 'shopping',
    title: "I'm shopping for a roof",
    body: "New to OpenbooQ? Learn how pricing works, how we're different, and what to expect before you commit to anything.",
    Icon: Search,
    color: '#07B0E9', // brand cyan
    bullets: [
      'How transparent pricing works',
      'What makes OpenbooQ different',
      'Using insurance for storm damage',
    ],
    modal: {
      prompt: 'Want to learn how it works, or talk to a real person?',
      actions: [
        { kind: 'link', label: 'How It Works', href: '/how-it-works' },
        { kind: 'tel', label: `Call ${PHONE_DISPLAY}` },
      ],
    },
  },
  {
    id: 'in-progress',
    title: 'I have a project in progress',
    body: "Already signed? Track what's happening, understand your timeline, and know what to expect on install day.",
    Icon: Home,
    color: '#F59E0B', // amber
    bullets: [
      'How soon can you start',
      'Who installs my roof',
      "What if there's bad weather",
    ],
    modal: {
      prompt: 'Sign in to track your project, or call us direct.',
      actions: [
        { kind: 'portal', label: 'Sign In' },
        { kind: 'tel', label: `Call ${PHONE_DISPLAY}` },
      ],
    },
  },
  {
    id: 'warranty',
    title: 'I need warranty or post-install help',
    body: "Roof's done? File a warranty claim, get maintenance guidance, or report an issue. We don't disappear after the sale.",
    Icon: Shield,
    color: '#10B981', // green
    bullets: [
      'What your 5-year warranty covers',
      'How to file a warranty claim',
      'Report an issue with my roof',
    ],
    modal: {
      prompt: 'Log in to file a claim, or contact our team direct.',
      actions: [
        { kind: 'portal', label: 'Log In' },
        { kind: 'tel', label: `Contact ${PHONE_DISPLAY}` },
      ],
    },
  },
];

const Lane = ({ lane, onOpen }) => {
  const { title, body, Icon: LaneIcon, color, bullets, id } = lane;
  return (
    <button
      type="button"
      onClick={() => onOpen(id)}
      className="group rounded-2xl bg-white p-7 lg:p-8 flex flex-col text-left w-full transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl focus:outline-none focus-visible:ring-4 focus-visible:ring-cyan-400/40"
      style={{
        border: '1px solid #e5ebf0',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.06)',
      }}
    >
      {/* Icon badge — colored rounded square */}
      <div
        className="flex items-center justify-center rounded-xl mb-6 transition-transform duration-300 group-hover:scale-105"
        style={{
          width: '56px',
          height: '56px',
          background: color,
          boxShadow: `0 10px 24px ${color}33`,
        }}
        aria-hidden="true"
      >
        <LaneIcon size={26} strokeWidth={2.2} color="white" />
      </div>

      {/* Title */}
      <h3
        className="font-black mb-3"
        style={{
          fontSize: 'clamp(20px, 1.6vw, 22px)',
          color: '#0a1628',
          fontFamily: "'Poppins', 'Montserrat', sans-serif",
          letterSpacing: '-0.015em',
        }}
      >
        {title}
      </h3>

      {/* Body */}
      <p className="text-[14.5px] lg:text-[15px] text-gray-500 leading-relaxed mb-6">
        {body}
      </p>

      {/* Bullet rows — visual content only (whole card is the trigger).
          Arrows slide right slightly when the parent card is hovered. */}
      <ul className="space-y-2.5 mt-auto">
        {bullets.map((bullet) => (
          <li
            key={bullet}
            className="inline-flex items-center gap-2 text-[14px] lg:text-[14.5px] font-medium text-gray-800"
          >
            <ArrowRight
              size={16}
              strokeWidth={2.4}
              style={{ color }}
              className="flex-shrink-0 transition-transform duration-300 group-hover:translate-x-1"
              aria-hidden="true"
            />
            <span>{bullet}</span>
          </li>
        ))}
      </ul>
    </button>
  );
};

const LaneModal = ({ lane, onClose }) => {
  const { title, body, Icon: LaneIcon, color, modal } = lane;

  const handleAction = (action) => {
    if (action.kind === 'link') {
      window.location.href = action.href;
    } else if (action.kind === 'portal') {
      window.open(PORTAL_URL, '_blank', 'noopener,noreferrer');
    } else if (action.kind === 'tel') {
      window.location.href = `tel:${PHONE_TEL}`;
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-fadeIn"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lane-modal-title"
    >
      {/* Backdrop — click to close */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal card */}
      <div
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-7 lg:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button (X) — top right */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close dialog"
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors duration-200"
        >
          <X size={20} strokeWidth={2.2} />
        </button>

        {/* Header — icon badge + title */}
        <div className="flex items-center gap-3 mb-3 pr-8">
          <div
            className="flex items-center justify-center rounded-lg flex-shrink-0"
            style={{
              width: '44px',
              height: '44px',
              background: color,
              boxShadow: `0 8px 20px ${color}33`,
            }}
            aria-hidden="true"
          >
            <LaneIcon size={22} strokeWidth={2.2} color="white" />
          </div>
          <h3
            id="lane-modal-title"
            className="font-black text-lg lg:text-xl"
            style={{
              color: '#0a1628',
              fontFamily: "'Poppins', 'Montserrat', sans-serif",
              letterSpacing: '-0.015em',
            }}
          >
            {title}
          </h3>
        </div>

        {/* Prompt */}
        <p className="text-[14.5px] text-gray-600 leading-relaxed mb-6">
          {modal.prompt}
        </p>

        {/* 2 action buttons — stacks on mobile, row on sm+ */}
        <div className="flex flex-col sm:flex-row gap-3">
          {modal.actions.map((action, i) => {
            const isPrimary = i === 0;
            return (
              <button
                key={action.label}
                type="button"
                onClick={() => handleAction(action)}
                className={`flex-1 inline-flex items-center justify-center gap-2 font-bold px-5 py-3 rounded-xl text-[14px] lg:text-[15px] transition-all duration-200 hover:-translate-y-0.5 ${
                  isPrimary
                    ? 'text-white'
                    : 'text-gray-800'
                }`}
                style={
                  isPrimary
                    ? {
                        background: '#07B0E9',
                        boxShadow: '0 10px 22px rgba(7, 176, 233, 0.30)',
                      }
                    : {
                        background: 'white',
                        border: '1.5px solid #d1d5db',
                      }
                }
              >
                {action.kind === 'tel' && (
                  <Phone size={16} strokeWidth={2.4} aria-hidden="true" />
                )}
                {action.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const SupportChannels = () => {
  const [openLaneId, setOpenLaneId] = useState(null);
  const openLane = LANES.find((l) => l.id === openLaneId) || null;

  // ESC key + body-scroll lock while modal is open.
  useEffect(() => {
    if (!openLaneId) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') setOpenLaneId(null);
    };
    window.addEventListener('keydown', handler);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = prevOverflow;
    };
  }, [openLaneId]);

  return (
    <>
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          {/* Section header */}
          <div className="text-center mb-12 lg:mb-16">
            <div className="mb-5 flex justify-center">
              <div
                className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.14em]"
                style={{
                  background: 'rgba(7, 176, 233, 0.10)',
                  border: '1px solid rgba(7, 176, 233, 0.30)',
                  color: '#0a6eb8',
                }}
              >
                Start Here
              </div>
            </div>
            <h2
              className="font-black leading-[1.05] tracking-tight mb-4 mx-auto"
              style={{
                fontSize: 'clamp(32px, 4.5vw, 52px)',
                fontFamily: "'Poppins', 'Montserrat', sans-serif",
                letterSpacing: '-0.025em',
                color: '#0a1628',
                maxWidth: '760px',
              }}
            >
              What do you need help{' '}
              <span style={{ color: '#07B0E9' }}>with?</span>
            </h2>
            <p className="text-base lg:text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">
              Three quick lanes to get you to the right answer fast.
            </p>
          </div>

          {/* 3-card grid — stacks on mobile, 3-across on md+ */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {LANES.map((lane) => (
              <Lane key={lane.id} lane={lane} onOpen={setOpenLaneId} />
            ))}
          </div>
        </div>
      </section>

      {openLane && (
        <LaneModal lane={openLane} onClose={() => setOpenLaneId(null)} />
      )}
    </>
  );
};

export default SupportChannels;
